import { errorMessage, useTermsGate, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import { Button, Card, CardDescription, CardFooter, CardHeader, TermsUpdateDialog } from '@instanexus/ui';
import { setAccessToken } from '@instanexus/web-auth';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { isTermsAcceptanceRequired, leaveCentralSignIn } from '@/lib/central-session';
import { parseSignInRequest, type SignInRequest } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** Full-page navigation; a function so the unit test can observe it. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

type Phase = 'checking' | 'prompt' | 'error';

/**
 * The Terms interstitial on the central page: asked once, before the handoff. Reads `me` with the
 * access token from the central session; accepting leaves exactly as sign-in would.
 */
export function TermsPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const request = useMemo<SignInRequest>(() => parseSignInRequest(searchParams), [searchParams]);

  const [phase, setPhase] = useState<Phase>('checking');
  const [pageErrorCode, setPageErrorCode] = useState<string | null>(null);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null | undefined>(undefined);

  const check = useCallback(async (): Promise<void> => {
    setPhase('checking');
    setPageErrorCode(null);
    try {
      const session = await api.auth.refreshSession();
      if (session === undefined) {
        void navigate(`/login${location.search}`, { replace: true });
        return;
      }
      setAccessToken(session.accessToken);
      const me = await api.auth.me();
      if (!isTermsAcceptanceRequired(me.termsVersion)) {
        await leaveCentralSignIn(api, request, redirectTo);
        return;
      }
      setAcceptedVersion(me.termsVersion);
      setPhase('prompt');
    } catch (error: unknown) {
      setPageErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
      setPhase('error');
    }
  }, [api, location.search, navigate, request]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void check();
  }, [check]);

  const [isPending, setIsPending] = useState(false);
  const [hasError, setHasError] = useState(false);

  const accept = useCallback(async (): Promise<void> => {
    await api.auth.acceptTerms();
    await leaveCentralSignIn(api, request, redirectTo);
  }, [api, request]);

  const gate = useTermsGate(acceptedVersion, accept);

  if (phase === 'prompt') {
    return (
      <AuthCardLayout>
        <TermsUpdateDialog
          open
          copy={gate.copy}
          termsUrl={gate.termsUrl}
          privacyUrl={gate.privacyUrl}
          isPending={isPending}
          hasError={hasError}
          onAccept={() => {
            setIsPending(true);
            setHasError(false);
            gate.onAccept().then(
              () => {
                setIsPending(false);
              },
              () => {
                setIsPending(false);
                setHasError(true);
              },
            );
          }}
        />
      </AuthCardLayout>
    );
  }

  if (phase === 'error') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('central.unavailableTitle')}
            </h1>
            <CardDescription role="alert">
              {errorMessage(t, pageErrorCode ?? UNEXPECTED_FAILURE)}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              type="button"
              onClick={() => {
                void check();
              }}
            >
              {t('central.retry')}
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <p role="status" className="text-muted-foreground">
            {t('central.checking')}
          </p>
        </CardHeader>
      </Card>
    </AuthCardLayout>
  );
}
